/**
 * Entity-group filter pill row.
 *
 * Sticky across the four list pages (Articles, Announcements, Notifications
 * Queue, Social Drafts) — the pill selection persists in localStorage and
 * synchronises across tabs in the same window via a custom event.
 *
 * Pills:
 *   All        — no filter
 *   Coverage   — entity_groups.group_name = 'asx_coverage_customers'  (1 member)
 *   Prospects  — entity_groups.group_name = 'asx_coverage_prospects'  (10 members)
 *   Majors     — entity_groups.group_name = 'asx_general_watchlist'   (68 members)
 *   Picked     — today's triage selections (Sydney-local) that are NOT in
 *                any of the three manual groups above
 *
 * Backend mirror: `assayer-backend/src/assayer/database/entity_group_filter.py`
 * + `assayer-publishing-service/src/database/entity-group-filter.ts`.
 */

import { useEffect, useState } from 'react';

export type EntityGroupPill = 'all' | 'coverage' | 'prospects' | 'campaigns' | 'majors' | 'picked';

interface PillSpec {
  value: EntityGroupPill;
  label: string;
  title: string;
}

const PILLS: PillSpec[] = [
  { value: 'all',       label: 'All',       title: 'Show everything — no entity-group filter' },
  { value: 'coverage',  label: 'Coverage',  title: 'asx_coverage_customers (paid coverage)' },
  { value: 'prospects', label: 'Prospects', title: 'asx_coverage_prospects (sales-targeted)' },
  { value: 'campaigns', label: 'Campaigns', title: 'Companies in any active sales campaign' },
  { value: 'majors',    label: 'Majors',    title: 'asx_general_watchlist (broader monitored list)' },
  { value: 'picked',    label: 'Picked',    title: "Today's triage selections that aren't in any manual group" },
];

const STORAGE_KEY = 'assayer.entityGroupPill';
const CHANGE_EVENT = 'assayer:entity-group-changed';

function readStoredPill(): EntityGroupPill {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && PILLS.some(p => p.value === raw)) {
      return raw as EntityGroupPill;
    }
  } catch {
    // localStorage unavailable (private browsing, etc.) — fall through.
  }
  return 'all';
}

/**
 * Hook returning the current pill + a setter that persists to localStorage
 * AND broadcasts the change so other mounted pages re-render.
 *
 * Returns [pill, setPill]. Pill is always a valid PillSpec.value.
 */
export function useEntityGroupPill(): [EntityGroupPill, (next: EntityGroupPill) => void] {
  const [pill, setPillState] = useState<EntityGroupPill>(readStoredPill);

  const setPill = (next: EntityGroupPill) => {
    setPillState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore — state still updates in-tab
    }
    // Broadcast so any other mounted page using this hook re-renders.
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
  };

  useEffect(() => {
    const onChange = (e: Event) => {
      const next = (e as CustomEvent<EntityGroupPill>).detail;
      if (next && next !== pill) setPillState(next);
    };
    // Listen for changes from THIS window (same-tab pages within SPA navigation).
    window.addEventListener(CHANGE_EVENT, onChange);
    // Also listen for storage events from OTHER tabs.
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue && PILLS.some(p => p.value === e.newValue)) {
        setPillState(e.newValue as EntityGroupPill);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, [pill]);

  return [pill, setPill];
}

/**
 * Pill row UI. Drop into any list page above the date strip / search row.
 *
 * Pure presentation around `useEntityGroupPill` — no list-API knowledge here.
 * The caller reads the current pill via the same hook and passes it as the
 * `entity_group` query param to its list endpoint.
 */
export function EntityGroupFilter() {
  const [pill, setPill] = useEntityGroupPill();
  return (
    <div className="flex items-center gap-1.5 flex-wrap" role="tablist" aria-label="Entity-group filter">
      {PILLS.map(p => {
        const isActive = pill === p.value;
        return (
          <button
            key={p.value}
            role="tab"
            aria-selected={isActive}
            onClick={() => setPill(p.value)}
            title={p.title}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              isActive
                ? 'bg-primary-600 text-white'
                : 'bg-surface text-grey-700 border border-grey-300 hover:bg-grey-50'
            }`}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}
